import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Card, Button, Tag, Space, Modal, Input, message, Image, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';
import { carsApi } from '../api/client';
import { maskPhoneNumber } from '../utils/format';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CarCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
`;

const CarInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 12px;
`;

export function PendingCars() {
  const queryClient = useQueryClient();
  const [selectedCar, setSelectedCar] = useState<any>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const { data: cars, isLoading } = useQuery({
    queryKey: ['pendingCars'],
    queryFn: async () => {
      const res = await carsApi.getPending();
      return res.data.data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: (carId: string) => carsApi.updateStatus(carId, 'APPROVED'),
    onSuccess: () => {
      message.success("E'lon tasdiqlandi!");
      queryClient.invalidateQueries({ queryKey: ['pendingCars'] });
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ carId, reason }: { carId: string; reason: string }) =>
      carsApi.updateStatus(carId, 'REJECTED', reason),
    onSuccess: () => {
      message.success("E'lon rad etildi");
      queryClient.invalidateQueries({ queryKey: ['pendingCars'] });
      setRejectModalOpen(false);
      setRejectReason('');
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const handleApprove = (car: any) => {
    Modal.confirm({
      title: "E'lonni tasdiqlaysizmi?",
      content: `${car.brand} ${car.model} - ${car.price} ${car.currency}`,
      okText: 'Tasdiqlash',
      cancelText: 'Bekor qilish',
      onOk: () => approveMutation.mutate(car.id),
    });
  };

  const handleReject = (car: any) => {
    setSelectedCar(car);
    setRejectModalOpen(true);
  };

  const columns = [
    {
      title: 'Mashina',
      key: 'car',
      render: (_: any, record: any) => (
        <div>
          <Text strong>{record.brand} {record.model}</Text>
          <br />
          <Text type="secondary">{record.year}</Text>
        </div>
      ),
    },
    {
      title: 'Narxi',
      key: 'price',
      render: (_: any, record: any) => (
        <Text>{record.price} {record.currency}</Text>
      ),
    },
    {
      title: 'Foydalanuvchi',
      key: 'user',
      render: (_: any, record: any) => (
        <Text>@{record.user?.username || record.user?.firstName || 'Noma\'lum'}</Text>
      ),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record)}
            loading={approveMutation.isPending}
          >
            Tasdiqlash
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => handleReject(record)}
          >
            Rad etish
          </Button>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedCar(record);
              setDetailModalOpen(true);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Kutayotgan e'lonlar</Title>

      <Table
        columns={columns}
        dataSource={cars || []}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Reject Modal */}
      <Modal
        title="Rad etish sababi"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={() => {
          if (!rejectReason.trim()) {
            message.warning('Sababni kiriting');
            return;
          }
          rejectMutation.mutate({ carId: selectedCar?.id, reason: rejectReason });
        }}
        okText="Rad etish"
        cancelText="Bekor qilish"
        confirmLoading={rejectMutation.isPending}
      >
        <TextArea
          rows={4}
          placeholder="Rad etish sababini yozing..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`${selectedCar?.brand} ${selectedCar?.model}`}
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={null}
        width={600}
      >
        {selectedCar && (
          <div>
            {selectedCar.collageImage && (
              <Image
                src={selectedCar.collageImage}
                alt="Car"
                style={{ width: '100%', borderRadius: 8, marginBottom: 16 }}
              />
            )}
            <CarInfo>
              <div><Text type="secondary">Yili:</Text> {selectedCar.year}</div>
              <div><Text type="secondary">Narxi:</Text> {selectedCar.price} {selectedCar.currency}</div>
              <div><Text type="secondary">Yonilg'i:</Text> {selectedCar.fuelType}</div>
              <div><Text type="secondary">Korobka:</Text> {selectedCar.transmission}</div>
              <div><Text type="secondary">Km:</Text> {selectedCar.mileage}</div>
              <div><Text type="secondary">Rang:</Text> {selectedCar.color}</div>
              <div><Text type="secondary">Joylashuv:</Text> {selectedCar.region}</div>
              <div><Text type="secondary">Telefon:</Text> {maskPhoneNumber(selectedCar.contactPhone)}</div>
            </CarInfo>
            {selectedCar.description && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Qo'shimcha:</Text>
                <p>{selectedCar.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
